import { useState, FormEvent, useContext } from "react";
import { getTokenForWeb, userLoginOps, getCustomerDetailsByToken } from "../services/auth";
import { encrypt, validUserLogin } from "../utils/Util";
import { IUserLogin } from "../models/IUserLogin";
import { Context } from "../utils/AppContext";

interface Props {
  onSuccess: () => void;
  onClose?: () => void;
}

const getOrCreateDeviceId = (): string => {
  const stored = localStorage.getItem("deviceId");
  if (stored) return stored;
  const newId = crypto.randomUUID();
  localStorage.setItem("deviceId", newId);
  return newId;
};

export function LoginPopup({ onSuccess, onClose }: Props) {
  const context = useContext(Context);
  const [loading, setLoading]       = useState(false);
  const [showPass, setShowPass]     = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [userdata, setUserdata]     = useState<IUserLogin>({
    email: "",
    password: "",
    deviceId: getOrCreateDeviceId(),
  });

  const handleSubmit = (evt: FormEvent) => {
    evt.preventDefault();
    if (!validUserLogin(userdata.email, userdata.password)) return;
    setLoading(true);
    setErrorMessage("");

    getTokenForWeb()
      .then((res) => {
        if (res?.data) localStorage.setItem("MA", encrypt(JSON.stringify(res.data)));
      })
      .catch(() => {});

    userLoginOps(userdata)
      .then(async (res) => {
        const tokenData = res.data;
        localStorage.setItem("user", encrypt(JSON.stringify(tokenData)));
        try {
          const detailsRes = await getCustomerDetailsByToken();
          const merged = { ...tokenData, ...detailsRes.data };
          localStorage.setItem("user", encrypt(JSON.stringify(merged)));
          context.setName(merged as any);
        } catch {
          context.setName(tokenData);
        }
        onSuccess();
      })
      .catch((err) => {
        const data = err?.response?.data;
        const apiMsg = Array.isArray(data) && data[0]?.description
          ? data[0].description
          : err.message;
        setErrorMessage(apiMsg);
      })
      .finally(() => setLoading(false));
  };

  return (
    <div
      style={{
        position: "fixed", inset: 0, zIndex: 2000,
        background: "rgba(0,0,0,0.55)", backdropFilter: "blur(4px)",
        display: "flex", alignItems: "center", justifyContent: "center",
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div style={{
        width: "min(94vw, 400px)", background: "#fff",
        borderRadius: 20, boxShadow: "0 8px 40px rgba(0,0,0,0.18)",
        overflow: "hidden",
      }}>
        {/* Header */}
        <div style={{
          background: "linear-gradient(135deg, #22c55e, #16a34a)",
          padding: "20px 24px", color: "#fff",
          display: "flex", alignItems: "center", justifyContent: "space-between",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{
              width: 38, height: 38, borderRadius: 10,
              background: "rgba(255,255,255,0.2)",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <i className="bi bi-shop-window" style={{ fontSize: "1.1rem" }}></i>
            </div>
            <div>
              <div style={{ fontWeight: 800, fontSize: "1rem" }}>Oturum Süresi Doldu</div>
              <div style={{ fontSize: "0.75rem", opacity: 0.85 }}>Devam etmek için tekrar giriş yapın</div>
            </div>
          </div>
          {onClose && (
            <button
              onClick={onClose}
              style={{ background: "rgba(255,255,255,0.15)", border: "none", borderRadius: 8, color: "#fff", cursor: "pointer", width: 30, height: 30, display: "flex", alignItems: "center", justifyContent: "center" }}
            >
              <i className="bi bi-x-lg"></i>
            </button>
          )}
        </div>

        <div style={{ padding: "24px" }}>
          {errorMessage && (
            <div style={{
              display: "flex", alignItems: "center", gap: 8,
              background: "#fef2f2", border: "1.5px solid #fecaca",
              color: "#b91c1c", fontSize: "0.84rem",
              borderRadius: 10, padding: "10px 14px", marginBottom: 16,
            }}>
              <i className="bi bi-exclamation-circle-fill" style={{ flexShrink: 0 }}></i>
              <span style={{ flex: 1 }}>{errorMessage}</span>
              <button onClick={() => setErrorMessage("")} style={{ background: "none", border: "none", color: "#b91c1c", cursor: "pointer", padding: 0 }}>
                <i className="bi bi-x-lg"></i>
              </button>
            </div>
          )}

          <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            {/* Email */}
            <div>
              <label style={{ fontSize: "0.78rem", fontWeight: 600, color: "#374151", display: "block", marginBottom: 5 }}>
                E-posta
              </label>
              <div style={{ display: "flex" }}>
                <span style={{
                  display: "flex", alignItems: "center", padding: "0 12px",
                  background: "#f9fafb", border: "1.5px solid #e5e7eb",
                  borderRight: "none", borderRadius: "10px 0 0 10px", color: "#9ca3af",
                }}>
                  <i className="bi bi-envelope-fill"></i>
                </span>
                <input
                  type="email"
                  value={userdata.email}
                  onChange={(e) => setUserdata({ ...userdata, email: e.target.value })}
                  placeholder="ornek@email.com"
                  required
                  style={{
                    flex: 1, padding: "10px 14px",
                    border: "1.5px solid #e5e7eb", borderLeft: "none",
                    borderRadius: "0 10px 10px 0", fontSize: "0.9rem",
                    outline: "none", color: "#111827",
                  }}
                />
              </div>
            </div>

            {/* Şifre */}
            <div>
              <label style={{ fontSize: "0.78rem", fontWeight: 600, color: "#374151", display: "block", marginBottom: 5 }}>
                Şifre
              </label>
              <div style={{ display: "flex" }}>
                <span style={{
                  display: "flex", alignItems: "center", padding: "0 12px",
                  background: "#f9fafb", border: "1.5px solid #e5e7eb",
                  borderRight: "none", borderRadius: "10px 0 0 10px", color: "#9ca3af",
                }}>
                  <i className="bi bi-lock-fill"></i>
                </span>
                <input
                  type={showPass ? "text" : "password"}
                  value={userdata.password}
                  onChange={(e) => setUserdata({ ...userdata, password: e.target.value })}
                  placeholder="••••••••"
                  required
                  style={{
                    flex: 1, padding: "10px 14px",
                    border: "1.5px solid #e5e7eb", borderLeft: "none", borderRight: "none",
                    fontSize: "0.9rem", outline: "none", color: "#111827",
                  }}
                />
                <button
                  type="button"
                  onClick={() => setShowPass(!showPass)}
                  style={{
                    padding: "0 12px", background: "#f9fafb",
                    border: "1.5px solid #e5e7eb", borderLeft: "none",
                    borderRadius: "0 10px 10px 0", color: "#9ca3af", cursor: "pointer",
                  }}
                >
                  <i className={`bi ${showPass ? "bi-eye-slash-fill" : "bi-eye-fill"}`}></i>
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              style={{
                marginTop: 4, padding: "11px",
                background: loading ? "#86efac" : "linear-gradient(135deg, #22c55e, #16a34a)",
                color: "#fff", fontWeight: 700, fontSize: "0.95rem",
                border: "none", borderRadius: 10, cursor: loading ? "not-allowed" : "pointer",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                boxShadow: "0 4px 14px rgba(34,197,94,0.3)",
              }}
            >
              {loading
                ? <><span className="spinner-border spinner-border-sm"></span>Giriş yapılıyor...</>
                : <><i className="bi bi-box-arrow-in-right"></i>Giriş Yap</>
              }
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
